import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BookOpen, ChevronLeft, ChevronRight } from "lucide-react";

const PLATFORMS = [
  { slug: "", label: "All" },
  { slug: "myntra", label: "Myntra" },
  { slug: "nykaa", label: "Nykaa" },
  { slug: "ajio", label: "AJIO" },
  { slug: "blinkit", label: "Blinkit" },
  { slug: "swiggy-instamart", label: "Swiggy Instamart" },
  { slug: "zepto", label: "Zepto" },
  { slug: "flipkart-minutes", label: "Flipkart Minutes" },
  { slug: "tata-cliq", label: "Tata CLiQ" },
  { slug: "flipkart", label: "Flipkart" },
  { slug: "amazon", label: "Amazon" },
  { slug: "meesho", label: "Meesho" },
  { slug: "firstcry", label: "FirstCry" },
];

export default function GuidesHub() {
  const [params, setParams] = useSearchParams();
  const platform = params.get("platform") || "";
  const page = parseInt(params.get("page") || "1", 10);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams({ page: String(page), limit: "120" });
    if (platform) q.set("platform", platform);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/blogs?${q}`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [platform, page]);

  const totalPages = Math.max(1, Math.ceil(data.total / 120));
  const setPage = (p) => setParams(platform ? { platform, page: String(p) } : { page: String(p) });

  return (
    <div className="min-h-screen bg-white pt-28 pb-20 px-4">
      <Helmet>
        <title>{`Marketplace Seller Guides${platform ? ` — ${PLATFORMS.find((x) => x.slug === platform)?.label}` : ""} | BrandBorn`}</title>
        <meta name="description" content="11,000+ expert guides on Myntra, Nykaa, AJIO, Blinkit, Zepto, Swiggy Instamart, Tata CLiQ, Flipkart Minutes, Amazon, Flipkart, Meesho and FirstCry — onboarding, listing, account management, ads and seller support across every tier-1 and tier-2 city in India." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.brandborn.in/guides" />
      </Helmet>
      <div className="max-w-6xl mx-auto">
        <div className="mb-10">
          <div className="flex items-center gap-3 text-red-500 font-semibold mb-3"><BookOpen size={22} /> Knowledge Library</div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Marketplace Seller Guides</h1>
          <p className="text-gray-600 max-w-3xl">Expert guides on marketplace onboarding, seller registration, product listing, account management, advertising and seller support — for every major Indian platform and every tier-1 & tier-2 city. By BrandBorn: 1,000+ brands onboarded, 70+ platforms, 98% success ratio.</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="guides-platform-filters">
          {PLATFORMS.map((p) => (
            <button
              key={p.slug || "all"}
              data-testid={`guides-filter-${p.slug || "all"}`}
              onClick={() => setParams(p.slug ? { platform: p.slug, page: "1" } : { page: "1" })}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${platform === p.slug ? "bg-red-500 text-white border-red-500" : "bg-white text-gray-700 border-gray-300 hover:border-red-400"}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-gray-500 py-20 text-center animate-pulse" data-testid="guides-loading">Loading guides…</div>
        ) : (
          <>
            <div className="text-sm text-gray-500 mb-4" data-testid="guides-count">{data.total.toLocaleString()} guides · Page {page} of {totalPages}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="guides-grid">
              {data.items.map((it) => (
                <Link key={it.slug} to={`/blog/${it.slug}`} className="block border border-gray-200 rounded-xl p-4 hover:border-red-400 hover:shadow-md transition group">
                  <div className="text-xs text-red-500 font-semibold mb-1">{it.category}{it.city ? ` · ${it.city}` : ""}</div>
                  <div className="text-sm font-semibold text-gray-900 group-hover:text-red-500 transition leading-snug">{it.title}</div>
                </Link>
              ))}
            </div>
            <div className="flex items-center justify-center gap-4 mt-10">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="guides-prev-btn" className="flex items-center gap-1 px-5 py-2 rounded-full border border-gray-300 font-semibold text-sm disabled:opacity-40 hover:border-red-400 transition"><ChevronLeft size={16} /> Prev</button>
              <span className="text-sm text-gray-600">Page {page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="guides-next-btn" className="flex items-center gap-1 px-5 py-2 rounded-full border border-gray-300 font-semibold text-sm disabled:opacity-40 hover:border-red-400 transition">Next <ChevronRight size={16} /></button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
